import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CoachStudentNoteService } from '../../../../core/services/coach-student-note.service';
import { ICoachStudentNote } from '../../../../core/models/student-note.model';

const PAGE_SIZE = 10;
const MAX_LENGTH = 4000;

/**
 * The notes a coach keeps about a student: written here, listed here, and visible to nobody else
 * but the management. They are not tied to a meeting — the notes attached to a single appointment
 * are a separate thing the student can read, and they live with the calendar.
 */
@Component({
  selector: 'coach-student-notes-panel',
  templateUrl: './student-notes-panel.component.html',
  styleUrl: './student-notes-panel.component.scss',
})
export class StudentNotesPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() studentId!: number;

  notes: ICoachStudentNote[] = [];
  page = 1;
  lastPage = 1;
  total = 0;

  maxLength = MAX_LENGTH;

  fg: FormGroup = this.formBuilder.group({
    content: [null, [Validators.required, Validators.maxLength(MAX_LENGTH)]],
  });

  searchControl = new FormControl<string>('');

  /** The note being rewritten in place, with its own control so a cancel restores the original. */
  editingNoteId?: number;
  editControl = new FormControl<string>('', [Validators.required, Validators.maxLength(MAX_LENGTH)]);

  isLoading = false;
  isSaving = false;
  isSubmitted = false;
  editIsSaving = false;

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private noteService: CoachStudentNoteService,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroyed$), debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.page = 1;
        this.getNotes();
      });
  }

  ngOnChanges(): void {
    if (this.studentId) {
      this.page = 1;
      this.notes = [];
      this.getNotes();
    }
  }

  remainingLength(): number {
    return MAX_LENGTH - (this.fg.value.content?.length ?? 0);
  }

  hasMore(): boolean {
    return this.page < this.lastPage;
  }

  getNotes(append = false) {
    const parts = [`page=${this.page}`, `size=${PAGE_SIZE}`];
    const search = this.searchControl.value?.trim();
    if (search) {
      parts.push(`search=${encodeURIComponent(search)}`);
    }

    this.isLoading = true;
    this.noteService.getNotes(this.studentId, `?${parts.join('&')}`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const result = (r.data?.result ?? []) as ICoachStudentNote[];
        this.notes = append ? [...this.notes, ...result] : result;
        this.total = r.data?.meta?.total ?? 0;
        this.lastPage = r.data?.meta?.lastPage ?? 1;
      });
  }

  loadMore() {
    this.page += 1;
    this.getNotes(true);
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.fg.invalid) {
      return;
    }

    this.isSaving = true;
    this.noteService.createNote(this.studentId, this.fg.value.content)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isSaving = false))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('students.meeting_notes.private.saved'));
        this.isSubmitted = false;
        this.fg.reset();
        // A new note belongs at the top, so the list restarts rather than appends.
        this.page = 1;
        this.getNotes();
      });
  }

  startEdit(note: ICoachStudentNote) {
    this.editingNoteId = note.id;
    this.editControl.setValue(note.content);
  }

  cancelEdit() {
    this.editingNoteId = undefined;
    this.editControl.reset();
  }

  saveEdit(note: ICoachStudentNote) {
    if (this.editControl.invalid) {
      return;
    }

    this.editIsSaving = true;
    this.noteService.updateNote(note.id, this.editControl.value)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.editIsSaving = false))
      .subscribe(() => {
        note.content = this.editControl.value;
        this.toastrService.success(this.translate.instant('students.meeting_notes.private.updated'));
        this.cancelEdit();
      });
  }

  /**
   * Asked directly rather than through the [swal] directive. SweetAlert2 is pulled in on demand so
   * it stays out of the initial bundle.
   */
  async confirmDelete(note: ICoachStudentNote) {
    const Swal = (await import('sweetalert2')).default;

    const result = await Swal.fire({
      text: this.translate.instant('students.meeting_notes.private.delete_confirm'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('buttons.yes'),
      cancelButtonText: this.translate.instant('buttons.cancel_l'),
      heightAuto: false,
    });

    if (result.isConfirmed) {
      this.deleteNote(note);
    }
  }

  deleteNote(note: ICoachStudentNote) {
    this.noteService.deleteNote(note.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('students.meeting_notes.private.deleted'));
        this.notes = this.notes.filter(current => current.id !== note.id);
        this.total = Math.max(0, this.total - 1);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
